import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatISODate from '../../../Helpers/formatISODate'
import './styles.scss'

const STEP_ICONS = {
  box: solid('box'),
  truck: solid('truck'),
  'location-dot': solid('location-dot'),
  'circle-check': solid('circle-check'),
}

const STEPS = [
  { key: 'Picked Up', label: 'Picked Up', icon: 'box' },
  { key: 'Active', label: 'In Transit', icon: 'truck' },
  { key: 'Arrived', label: 'At Destination', icon: 'location-dot' },
  { key: 'Delivered', label: 'Delivered', icon: 'circle-check' },
]

const STATUS_STEP_MAP = {
  'Picked Up': 0,
  Active: 1,
  Arrived: 2,
  Delivered: 3,
  'On hold': 1,
  Inactive: -1,
}

const STATUS_BADGE = {
  Active: { label: 'In Transit', color: '#2563EB' },
  'Picked Up': { label: 'Picked Up', color: '#0D9488' },
  Arrived: { label: 'At Destination', color: '#7C3AED' },
  Delivered: { label: 'Delivered', color: '#16A34A' },
  'On hold': { label: 'On Hold', color: '#D97706' },
  Inactive: { label: 'Inactive', color: '#6B7280' },
}

// Show only the city/country portion of an address (everything after the last comma,
// or the full string if no comma found)
const maskAddress = (address) => {
  if (!address) return '—'
  const parts = address.split(',').map((p) => p.trim())
  if (parts.length === 1) return parts[0]
  return parts.slice(-2).join(', ')
}

const TrackItResult = ({ trackItResult }) => {
  const currentStep = STATUS_STEP_MAP[trackItResult.status] ?? 0
  const badge = STATUS_BADGE[trackItResult.status] ?? { label: trackItResult.status, color: '#6B7280' }
  const isOnHold = trackItResult.status === 'On hold'

  return (
    <div className="track-it-result">
      <span className="st">
        <FontAwesomeIcon className="icon" icon={solid('caret-right')} />{' '}
        Shipment Tracking
      </span>

      <div className="track-it-result__content">

        {/* Header */}
        <div className="result-header">
          <div className="result-header__left">
            <span className="result-header__label">Tracking Number</span>
            <span className="result-header__number">{trackItResult.trackingNumber}</span>
          </div>
          <span className="result-header__badge" style={{ backgroundColor: badge.color }}>
            {badge.label}
          </span>
        </div>

        {/* Progress steps */}
        <div className="result-progress">
          {STEPS.map((step, idx) => {
            const isDone = !isOnHold && currentStep > idx
            const isActive = currentStep === idx
            return (
              <div key={step.key} className="result-progress__step">
                <div className={`result-progress__step__circle${isDone ? ' done' : isActive ? ' active' : ''}`}>
                  <FontAwesomeIcon icon={STEP_ICONS[step.icon]} />
                </div>
                <span className={`result-progress__step__label${isDone || isActive ? ' active-label' : ''}`}>
                  {step.label}
                </span>
                {idx < STEPS.length - 1 && (
                  <div className={`result-progress__step__line${isDone ? ' done' : ''}`} />
                )}
              </div>
            )
          })}
        </div>

        {isOnHold && (
          <div className="result-hold-notice">
            <FontAwesomeIcon icon={solid('triangle-exclamation')} className="hold-icon" />
            This shipment is currently on hold. Please contact us for more information.
          </div>
        )}

        {/* Shipment summary row */}
        <div className="result-summary">
          <div className="result-summary__item">
            <span className="result-summary__item__label">Dispatch Date</span>
            <span className="result-summary__item__value">
              {formatISODate(trackItResult.dispatchDate)?.split(' ')[0] || '—'}
            </span>
          </div>
          <div className="result-summary__item">
            <span className="result-summary__item__label">Est. Delivery</span>
            <span className="result-summary__item__value">
              {formatISODate(trackItResult.estimatedDeliveryDate)?.split(' ')[0] || '—'}
            </span>
          </div>
          <div className="result-summary__item">
            <span className="result-summary__item__label">Origin</span>
            <span className="result-summary__item__value">
              {trackItResult.dispatchLocation || '—'}
            </span>
          </div>
          <div className="result-summary__item">
            <span className="result-summary__item__label">Current Location</span>
            <span className="result-summary__item__value">
              {trackItResult.currentLocation || '—'}
            </span>
          </div>
          <div className="result-summary__item">
            <span className="result-summary__item__label">Description</span>
            <span className="result-summary__item__value">
              {trackItResult.packageDescription || '—'}
            </span>
          </div>
        </div>

        {/* Sender / Recipient cards — contact details hidden, addresses masked */}
        <div className="result-parties">
          <div className="result-parties__card">
            <h4 className="result-parties__card__title">
              <FontAwesomeIcon icon={solid('user')} className="party-icon" />
              Sender
            </h4>
            <div className="result-parties__card__row">
              <span>Name</span>
              <span>{trackItResult.senderName || '—'}</span>
            </div>
            <div className="result-parties__card__row">
              <span>Location</span>
              <span>{maskAddress(trackItResult.senderAddress)}</span>
            </div>
          </div>

          <div className="result-parties__card">
            <h4 className="result-parties__card__title">
              <FontAwesomeIcon icon={solid('user-check')} className="party-icon" />
              Recipient
            </h4>
            <div className="result-parties__card__row">
              <span>Name</span>
              <span>{trackItResult.receiverName || '—'}</span>
            </div>
            <div className="result-parties__card__row">
              <span>Location</span>
              <span>{maskAddress(trackItResult.receiverAddress)}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default TrackItResult
